import { FC, useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import toast from "react-hot-toast";
import { Link } from "react-router-dom";
import img1 from "../assets/img1.webp";
import { Article, articles } from "../data/blogArticles";
import { supabase } from "../supabaseClient";
import "./ArticlesSection.css";

const BLOG_BREADCRUMB_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    {
      "@type": "ListItem",
      position: 1,
      name: "Home",
      item: "https://astroaura.ai/",
    },
    {
      "@type": "ListItem",
      position: 2,
      name: "Blog",
      item: "https://astroaura.ai/blog",
    },
  ],
};

interface BlogPost {
  id: number;
  title: string;
  content: string;
  excerpt: string;
  slug: string;
  featured_image_url?: string;
  published: boolean;
  created_at: string;
  updated_at: string;
}

// NOTE: there was a sixth entry here, "Mercury Retrograde 2024: Complete Guide
// to Navigate the Cosmic Storm". It had no `path`, and the card handlers fall
// back to navigate("/"), so clicking the FIRST article on /blog dumped the
// visitor on the homepage. No Mercury Retrograde article exists on the site, so
// the card was advertising content we do not have. Removed rather than pointed
// at an unrelated article. If we want that piece, write it and add it back with
// a real path.
//
// Every remaining entry MUST have a `path`. The `|| "/"` fallback is gone, so a
// missing path now fails loudly in review instead of silently redirecting home.
/* Moved to src/data/blogArticles.ts so the homepage strip renders the same
   four articles. */


const ArticlesSection: FC = () => {
  const [blogPosts, setBlogPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("All");

  /* Astrotalk-style hub: category sidebar + search over the static articles.
     Default state (All, empty query) renders every card, so the prerendered
     HTML keeps all links crawlable; filtering is a client-side enhancement. */
  const categories = useMemo(() => {
    const counts = new Map<string, number>();
    articles.forEach(
      (a) => a.tag && counts.set(a.tag, (counts.get(a.tag) || 0) + 1)
    );
    return [
      "All",
      ...Array.from(counts.entries())
        .sort((x, y) => y[1] - x[1])
        .map(([t]) => t),
    ];
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return articles.filter(
      (a) =>
        (category === "All" || a.tag === category) &&
        (!q || `${a.title} ${a.desc}`.toLowerCase().includes(q))
    );
  }, [query, category]);

  useEffect(() => {
    fetchPublishedBlogs();
  }, []);

  // react-snap prerenders this route in a headless browser where the Supabase
  // fetch cannot succeed. The failure fired a toast, and react-snap then
  // serialised that toast element into the STATIC HTML, so "Failed to load blog
  // posts" shipped to production inside /blog/index.html and was visible to
  // crawlers and on first paint. Same detection the guest-chat hook uses.
  const isPrerender = () =>
    typeof navigator !== "undefined" && navigator.userAgent === "ReactSnap";

  const fetchPublishedBlogs = async () => {
    try {
      setLoading(true);

      const { data, error } = await supabase
        .from("blogs")
        .select("*")
        .eq("published", true)
        .order("created_at", { ascending: false });

      if (error) throw error;

      setBlogPosts(data || []);
    } catch (err: any) {
      // Not surfaced in the UI: the static articles below are a complete
      // fallback, so an error banner would sit on top of a page that rendered
      // fine. Still reported to the console and as a toast for us.
      console.error("Error fetching published blogs:", err);
      if (!isPrerender()) toast.error("Failed to load blog posts");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const truncateText = (text: string, maxLength: number) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + "...";
  };

  if (loading) {
    return (
      <div className="articles-wrapper">
        <h1 className="Articles-section-title">Astrology Insights & Wisdom</h1>
        <p className="section-subtitle">
          Loading the latest cosmic insights...
        </p>
        <div style={{ textAlign: "center", padding: "50px" }}>
          <div style={{ fontSize: "18px", color: "#666" }}>Loading...</div>
        </div>
      </div>
    );
  }

  // Use dynamic blogs if available, otherwise fall back to static articles
  const displayContent = blogPosts.length > 0;
  // featuredBlog / otherBlogs / staticArticles used to live here. They were
  // only referenced by the commented-out layout below, and staticArticles was
  // `articles.slice(1)`, which silently dropped whatever the first article was.

  // return (
  //   <div className="articles-wrapper">
  //     <h1 className="Articles-section-title">Astrology Insights & Wisdom</h1>
  //     <p className="section-subtitle">
  //       {displayContent
  //         ? "Latest insights from our astrology experts - discover what the stars have in store."
  //         : "Discover the Mysteries of The Cosmos through our expertly crafted articles on astrology, zodiac signs, and celestial guidance."
  //       }
  //     </p>

  //     {error && (
  //       <div style={{
  //         backgroundColor: '#fee',
  //         color: '#c33',
  //         padding: '10px',
  //         borderRadius: '5px',
  //         margin: '20px 0',
  //         textAlign: 'center'
  //       }}>
  //         {error}
  //       </div>
  //     )}

  //     <div className="articles-grid">
  //       {/* Featured article/blog */}
  //       {displayContent && featuredBlog ? (
  //         <div className="articles-card big">
  //           <img
  //             src={featuredBlog.featured_image_url || img1}
  //             alt={featuredBlog.title}
  //             onClick={() => handleBlogClick(featuredBlog.slug)}
  //             style={{ cursor: "pointer" }}
  //           />
  //           <div className="articles-content">
  //             <h2
  //               onClick={() => handleBlogClick(featuredBlog.slug)}
  //               style={{ cursor: "pointer" }}
  //             >
  //               {featuredBlog.title}
  //             </h2>
  //             <p>{truncateText(featuredBlog.excerpt || featuredBlog.content, 200)}</p>
  //             <button onClick={() => handleBlogClick(featuredBlog.slug)} className="card-button">Read Full Article</button>
  //           </div>
  //         </div>
  //       ) : (
  //         <div className="articles-card big">
  //           <img src={articles[0].img} alt={articles[0].title} onClick={() => navigate("/BlogPage")}
  //             style={{ cursor: "pointer" }} />
  //           <div className="articles-content">
  //             <h2 onClick={() => navigate("/BlogPage")}
  //               style={{ cursor: "pointer" }}>{articles[0].title}</h2>
  //             <p>{articles[0].desc}</p>
  //             <button onClick={() => navigate("/BlogPage")} className="card-button">{articles[0].btn}</button>
  //           </div>
  //         </div>
  //       )}

  //       {/* Other articles/blogs */}
  //       {displayContent && otherBlogs ? (
  //         otherBlogs.map((blog: BlogPost) => (
  //           <div className="articles-card" key={blog.id}>
  //             <img
  //               src={blog.featured_image_url || img2}
  //               alt={blog.title}
  //               onClick={() => handleBlogClick(blog.slug)}
  //               style={{ cursor: "pointer" }}
  //             />
  //             <div className="articles-content">
  //               <span className="tag">Blog</span>
  //               <span className="Dates">{formatDate(blog.created_at)}</span>
  //               <h3
  //                 onClick={() => handleBlogClick(blog.slug)}
  //                 style={{ cursor: "pointer" }}
  //               >
  //                 {blog.title}
  //               </h3>
  //               <p>{truncateText(blog.excerpt || blog.content, 120)}</p>
  //               <button onClick={() => handleBlogClick(blog.slug)} className="card-button">Read More</button>
  //             </div>
  //           </div>
  //         ))
  //       ) : null}

  //       {!displayContent && staticArticles ? (
  //         staticArticles.map((item: Article) => (
  //           <div className="articles-card" key={item.id}>
  //             <img
  //               src={item.img}
  //               alt={item.title}
  //               onClick={() => navigate(item.path!)}
  //               style={{ cursor: "pointer" }}
  //             />
  //             <div className="articles-content">
  //               {item.tag && <span className="tag">{item.tag}</span>}
  //               {item.date && <span className="Dates">{item.date}</span>}
  //               <h3
  //                 onClick={() => navigate(item.path!)}
  //                 style={{ cursor: "pointer" }}
  //               >
  //                 {item.title}
  //               </h3>
  //               <p>{item.desc}</p>
  //               <button onClick={() => navigate(item.path!)} className="card-button">{item.btn}</button>
  //             </div>
  //           </div>
  //         ))
  //       ) : null}
  //     </div>

  //   </div>
  // );
  return (
    <>
      <Helmet>
        <title>Astrology Blog | Vedic Astrology Insights - Aura AI</title>
        <meta name="description" content="Read expert astrology articles on Vedic astrology, zodiac signs, planetary transits, Mahadasha, and more. Learn astrology from Aura AI experts." />
        <link rel="canonical" href="https://astroaura.ai/blog" />
        <meta property="og:image" content="https://astroaura.ai/og-image.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content="https://astroaura.ai/og-image.png" />
        <meta property="og:title" content="Astrology Blog | Vedic Astrology Insights - Aura AI" />
        <meta property="og:description" content="Read expert astrology articles on Vedic astrology, zodiac signs, and more." />
        <meta property="og:url" content="https://astroaura.ai/blog" />
        <meta property="og:type" content="website" />
        <script type="application/ld+json">
          {JSON.stringify(BLOG_BREADCRUMB_SCHEMA)}
        </script>
      </Helmet>
      <div className="articles-wrapper">
      <h1 className="Articles-section-title">Astrology Insights & Wisdom</h1>
      <p className="section-subtitle">
        {displayContent
          ? "Latest insights from our astrology experts - discover what the stars have in store."
          : "Discover the Mysteries of The Cosmos through our expertly crafted articles on astrology, zodiac signs, and celestial guidance."}
      </p>

      {/* Deliberately not rendered. When the Supabase fetch fails we fall back
          to the static articles below, which is a complete, working page, so
          "Failed to load blog posts" was a red error message shown on top of
          content that had loaded fine. It is currently visible on production
          and is in the prerendered HTML, so Google sees it too. The failure is
          still logged to the console and reported via toast for us. */}

      <div className="blogx-search" role="search">
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Let's find what you're looking for..."
          aria-label="Search blog posts"
        />
      </div>

      <div className="blogx-layout">
        <aside className="blogx-sidebar" aria-label="Blog categories">
          <p className="blogx-sidebar-title">Categories</p>
          {categories.map((c) => (
            <button
              key={c}
              type="button"
              className={`blogx-cat${category === c ? " active" : ""}`}
              onClick={() => setCategory(c)}
            >
              {c}
            </button>
          ))}
        </aside>

      <div className="articles-grid">
        {/* CASE 1: Dynamic Content (Supabase) */}
        {displayContent
          ? blogPosts.map((blog: BlogPost) => (
              <Link
                to={`/blog/${blog.slug}`}
                className="articles-card"
                key={blog.id}
                aria-label={blog.title}
              >
                <img
                  src={blog.featured_image_url || img1}
                  alt={blog.title}
                />
                <div className="articles-content">
                  <div className="card-meta">
                    <span className="tag">Blog</span>
                    <span className="Dates">{formatDate(blog.created_at)}</span>
                  </div>
                  <h3>{blog.title}</h3>
                  {/* Truncate text to keep cards uniform height */}
                  <p>{truncateText(blog.excerpt || blog.content, 120)}</p>
                  <span className="card-button">Read More</span>
                </div>
              </Link>
            ))
          : /* CASE 2: Static Fallback Content */
            /* Rendered as <Link>, not a div with onClick. Previously every card
               on this page was a click handler, so a crawler could not follow a
               single one: /blog passed zero link equity to the articles it
               exists to promote, and the article pages had no crawlable path in
               from the blog index at all. */
            filtered.map((item: Article) => (
              <Link
                to={item.path}
                className="articles-card"
                key={item.id}
                aria-label={item.title}
              >
                <img src={item.img} alt={item.title} />
                <div className="articles-content">
                  <div className="card-meta">
                    {item.tag && <span className="tag">{item.tag}</span>}
                    {item.date && <span className="Dates">{item.date}</span>}
                  </div>
                  <h3>{item.title}</h3>
                  <p>{truncateText(item.desc, 120)}</p>
                  <span className="card-button">{item.btn}</span>
                </div>
              </Link>
            ))}
      </div>
      </div>
    </div>
    </>
  );
};

export default ArticlesSection;
